import { defineStore } from 'pinia'
import { StorageManager } from '@/utils/storageOptimisations'
import { OwnerFinancialManager } from '@/services/npcFinManager'
import { ChocoboFactory } from '@/services/chocoboFactory'
import { OwnerFactory } from '@/services/ownerFactory'
import { JockeyFactory } from '@/services/jockeyFactory'
import { BackgroundRaceSimulator } from '@/utils/backgroundRaceSim'
import { MonthlyRaceManager } from '@/services/monthlyRaceManager'
import { PlayerOwner } from '@/models/playerOwner'
import { RetirementManager } from '@/services/retirementManager'
import { MEMORY_CONFIG } from '@/constants/memoryConfig'
import { ChocoboLazyLoader } from '@/services/chocoboLazyLoader'
import { MemoryManager } from '@/services/memoryManager'
import { HistoricalStatsService } from '@/services/historicalStatsService'
import { PerformanceMonitor } from '@/services/performanceMonitor'
import { BreedingPoolManager } from '@/services/breedingPoolManager'
import { PerformanceRecordsService } from '@/services/performanceRecordsService'
import { BreedingManager } from '@/services/breedingManager'
import { NPCBreedingManager } from '@/services/npcBreedingManager'
import { BidManager } from '@/services/bidManager'
import { AgingSystem } from '@/services/agingSystem'

export const useGameStore = defineStore('game', {
  state: () => ({
    MAX_PLAYER_CHOCOBOS: MEMORY_CONFIG.maxPlayerChocobos,
    chocobos: [],
    owners: [],
    jockeys: [],
    playerOwner: null,
    isLoaded: false,
    currentDay: 1,
    activeAuctions: [],
    recentTransactions: [],
    maxTransactionHistory: 20,
    monthEndRewards: null,
    retirementHistory: [],
    lastCleanupDay: 1,
    lastPopulationCheckDay: 1,
    breedingTransactions: [],
    breedingIncomeNotifications: [],
    monthlyRaceCount: null,
    lastRaceCountReset: 0,
    performanceRecords: PerformanceRecordsService.initializeRecords(),
    activeBreedings: [],
    completedBreedings: [],
    notifications: [],
    npcBreedingTransactions: [],
    activeBids: [],
    performanceNotifications: [],
    breedingStats: {
      totalBreedings: 0,
      successfulBreedings: 0,
      currentMonth: 0,
      averageOffspringLevel: 0,
      bestOffspringStats: null,
      colorBreakdown: {},
      monthlyBreedings: 0,
      lastResetDay: 1
    }
  }),

  actions: {
    initializeGame(playerName, ranchName, homeRegion) {
      // Initialize performance monitoring
      if (!PerformanceMonitor.isInitialized) {
        PerformanceMonitor.initialize()
        this.setupPerformanceCallbacks()
      }

      const savedGame = this.loadGame()
      if (savedGame) {
        this.restoreGame(savedGame)
        return
      }

      this.playerOwner = new PlayerOwner(playerName, ranchName, homeRegion)
      // Ensure player owner is marked as player
      this.playerOwner.isPlayer = true
      this.chocobos = ChocoboFactory.generateInitialChocobos(850)
      this.owners = OwnerFactory.generateInitialOwners(90, homeRegion)
      this.owners.push(this.playerOwner)
      this.jockeys = JockeyFactory.generateInitialJockeys(50)

      try {
        ChocoboFactory.distributeChocobosToOwners(this.chocobos, this.owners, this.playerOwner)
      } catch (error) {
        console.error('Failed to distribute chocobos:', error)
        this.chocobos = ChocoboFactory.generateInitialChocobos(850)
        ChocoboFactory.distributeChocobosToOwners(this.chocobos, this.owners, this.playerOwner)
      }

      this.isLoaded = true
      this.processFinancialManagement()
      this.saveGame()
    },

    saveGame() {
      const gameState = {
        chocobos: this.chocobos,
        owners: this.owners,
        jockeys: this.jockeys,
        currentDay: this.currentDay,
        activeAuctions: this.activeAuctions,
        recentTransactions: this.recentTransactions,
        retirementHistory: this.retirementHistory,
        lastCleanupDay: this.lastCleanupDay,
        lastPopulationCheckDay: this.lastPopulationCheckDay,
        breedingTransactions: this.breedingTransactions,
        breedingIncomeNotifications: this.breedingIncomeNotifications,
        monthlyRaceCount: this.monthlyRaceCount,
        lastRaceCountReset: this.lastRaceCountReset,
        performanceRecords: PerformanceRecordsService.exportRecords(this.performanceRecords),
        activeBreedings: this.activeBreedings,
        completedBreedings: this.completedBreedings,
        notifications: this.notifications,
        npcBreedingTransactions: this.npcBreedingTransactions,
        breedingStats: this.breedingStats,
        activeBids: this.activeBids,
        performanceNotifications: this.performanceNotifications
      }

      try {
        StorageManager.saveGame(gameState)
      } catch (error) {
        console.error('Failed to save game:', error)
        throw new Error('Unable to save game - storage limit reached')
      }
    },

    loadGame() {
      return StorageManager.loadGame()
    },

    restoreGame(savedState) {
      if (!savedState) throw new Error('Invalid save data')

      try {
        this.chocobos = savedState.chocobos
        this.owners = savedState.owners
        this.jockeys = savedState.jockeys
        this.currentDay = savedState.currentDay
        this.activeAuctions = savedState.activeAuctions || []
        this.recentTransactions = savedState.recentTransactions || []
        this.retirementHistory = savedState.retirementHistory || []
        this.lastCleanupDay = savedState.lastCleanupDay || this.currentDay
        this.lastPopulationCheckDay = savedState.lastPopulationCheckDay || this.currentDay
        this.breedingTransactions = savedState.breedingTransactions || []
        this.breedingIncomeNotifications = savedState.breedingIncomeNotifications || []
        this.monthlyRaceCount = savedState.monthlyRaceCount || null
        this.lastRaceCountReset = savedState.lastRaceCountReset || 0
        this.performanceRecords = savedState.performanceRecords 
          ? PerformanceRecordsService.importRecords(savedState.performanceRecords)
          : PerformanceRecordsService.initializeRecords()
        this.activeBreedings = savedState.activeBreedings || []
        this.completedBreedings = savedState.completedBreedings || []
        this.notifications = savedState.notifications || []
        this.npcBreedingTransactions = savedState.npcBreedingTransactions || []
        this.activeBids = savedState.activeBids || []
        this.performanceNotifications = savedState.performanceNotifications || []
        this.breedingStats = savedState.breedingStats || {
          totalBreedings: 0,
          successfulBreedings: 0,
          currentMonth: 0,
          averageOffspringLevel: 0,
          bestOffspringStats: null,
          colorBreakdown: {},
          monthlyBreedings: 0,
          lastResetDay: this.currentDay
        }
        
        this.playerOwner = this.owners.find(owner => owner.isPlayer)
        if (!this.playerOwner) {
          throw new Error('Player owner not found in save data')
        }
        
        // Ensure player owner always has isPlayer flag set
        this.playerOwner.isPlayer = true
        
        // Ensure inventory is preserved during restore
        if (!this.playerOwner.inventory) {
          this.playerOwner.inventory = []
        }

        // Initialize estimated ages for chocobos that don't have them
        this.initializeChocoboAges()
        
        // Validate and fix any invalid chocobo behavior IDs
        this.validateChocoboBehaviors()

        // Verify critical game state
        if (this.chocobos.length === 0 || this.owners.length === 0) {
          throw new Error('Critical game data missing after restore')
        }

        this.isLoaded = true
      } catch (error) {
        console.error('Failed to restore game:', error)
        throw new Error('Save data may be corrupted')
      }
    },

    advanceDay(isTransitDay = false) {
      const oldDay = this.currentDay
      this.currentDay++
      
      const isEndOfMonth = MonthlyRaceManager.isMonthEnd(oldDay, this.currentDay)
      
      if (isEndOfMonth) {
        const rewards = MonthlyRaceManager.processMonthEnd(this)
        if (rewards.length > 0) {
          this.monthEndRewards = rewards
        }
        // Reset monthly performance records at month end
        PerformanceRecordsService.resetMonthlyRecords(this.performanceRecords)
        
        // Force save after month-end processing to ensure prizes are persisted
        this.saveGame()
      }

      // Process retirements periodically
      if (this.currentDay - this.lastPopulationCheckDay >= MEMORY_CONFIG.populationCheckInterval) {
        this.processRetirements()
        this.lastPopulationCheckDay = this.currentDay
      }
      
      // Process breeding progress daily
      BreedingManager.processBreedings(this)
      
      // Process NPC breeding decisions daily
      NPCBreedingManager.processDailyBreeding(this)
      
      // Process bid system daily
      BidManager.processDailyBids(this)
      BidManager.generateNewBids(this)

      // Check global population cap
      if (this.chocobos.length > MEMORY_CONFIG.maxGlobalChocobos) {
        RetirementManager.checkGlobalPopulationCap(this)
        // Global population cap enforced if needed
      }

      // Reduce background race activity during transit to improve performance
      if (!isTransitDay) {
        BackgroundRaceSimulator.simulateBackgroundRaces(this)
      } else {
        // Limited background activity during transit (25% of normal races)
        const originalLimit = BackgroundRaceSimulator.DAILY_RACE_LIMIT
        BackgroundRaceSimulator.DAILY_RACE_LIMIT = Math.floor(originalLimit * 0.25)
        BackgroundRaceSimulator.simulateBackgroundRaces(this)
        BackgroundRaceSimulator.DAILY_RACE_LIMIT = originalLimit // Restore
      }
      this.processFinancialManagement()
      this.enforceRaceHistoryLimits()
      
      // Periodic memory management
      if (this.currentDay % MEMORY_CONFIG.cleanupIntervalDays === 0) {
        this.performMemoryCompression()
        this.performHistoricalDataCleanup()
        this.performBreedingDataCleanup()
      }

      // Continuous memory monitoring
      this.scheduleMemoryCheck()
      
      // Skip save during transit progression (handled by batch save)
      if (!isTransitDay) {
        this.saveGame()
      }
    },

    addAuctions(auctions) {
      const existingIds = new Set(this.activeAuctions.map(a => a.chocobo.id))
      const newAuctions = auctions.filter(auction => !existingIds.has(auction.chocobo.id))
      this.activeAuctions.push(...newAuctions)
    },

    addTransaction(transaction) {
      this.recentTransactions.unshift(transaction)
      if (this.recentTransactions.length > this.maxTransactionHistory) {
        this.recentTransactions = this.recentTransactions.slice(0, this.maxTransactionHistory)
      }
    },

    removeAuction(auctionId) {
      this.activeAuctions = this.activeAuctions.filter(a => a.chocobo.id !== auctionId)
    },

    handleAuctionPurchase(auction) {
      if (this.playerOwner.money < auction.basePrice) return false
      
      this.playerOwner.money -= auction.basePrice
      auction.owner.money += auction.basePrice

      const ownerIdx = auction.owner.chocoboIds.indexOf(auction.chocobo.id)
      if (ownerIdx !== -1) {
        auction.owner.chocoboIds.splice(ownerIdx, 1)
      }
      this.playerOwner.chocoboIds.push(auction.chocobo.id)

      this.removeAuction(auction.chocobo.id)
      return true
    },

    processFinancialManagement() {
      // Ensure player owner is valid before any financial operations
      if (!this.playerOwner) {
        this.playerOwner = this.owners.find(owner => owner.isPlayer)
        if (!this.playerOwner) {
          console.error('CRITICAL: Cannot find player owner - skipping financial management')
          return
        }
      }
      
      // Force ensure player flag is set
      this.playerOwner.isPlayer = true
      
      // Debug logging to track player owner state
      if (!this.playerOwner || !this.playerOwner.isPlayer) {
        console.error('WARNING: Player owner missing or invalid during financial management', {
          playerOwner: this.playerOwner,
          isPlayer: this.playerOwner?.isPlayer,
          id: this.playerOwner?.id
        })
      }
      
      const { sales, auctions } = OwnerFinancialManager.manageFinances(this)
      
      sales.forEach(sale => {
        // Final safety check: block any sale involving player
        if (sale?.seller?.id === this.playerOwner?.id || sale?.seller?.isPlayer) {
          console.error('CRITICAL: Blocked transaction selling player chocobo!', {
            sale,
            playerOwnerId: this.playerOwner?.id,
            sellerId: sale?.seller?.id,
            sellerIsPlayer: sale?.seller?.isPlayer
          })
          return
        }
        
        this.recentTransactions.push({
          ...sale,
          day: this.currentDay
        })
      })

      auctions.forEach(auction => {
        // Final safety check: block any auction from player
        if (auction?.owner?.id === this.playerOwner?.id || auction?.owner?.isPlayer) {
          console.error('CRITICAL: Blocked auction from player!', {
            auction,
            playerOwnerId: this.playerOwner?.id,
            ownerId: auction?.owner?.id,
            ownerIsPlayer: auction?.owner?.isPlayer
          })
          return
        }
        
        this.activeAuctions.push({
          ...auction,
          startDay: this.currentDay,
          endDay: this.currentDay + 3
        })
      })

      this.activeAuctions = this.activeAuctions.filter(
        auction => auction.endDay >= this.currentDay
      )

      this.recentTransactions = this.recentTransactions.slice(-10)
    },

    processRaceAftermath() {
      this.processFinancialManagement()
    },

    processRetirements() {
      const retirements = RetirementManager.processNPCRetirements(this)
      // Process retirements
      return retirements
    },

    enforceRaceHistoryLimits() {
      // Enforce race history limits on all chocobos
      this.chocobos.forEach(chocobo => {
        if (chocobo.raceHistory && chocobo.raceHistory.length > MEMORY_CONFIG.maxRaceHistoryPerChocobo) {
          chocobo.raceHistory = chocobo.raceHistory.slice(-MEMORY_CONFIG.maxRaceHistoryPerChocobo)
        }
      })
    },

    performMemoryCompression() {
      // Performing periodic memory compression
      
      // Use intelligent memory manager
      MemoryManager.checkMemoryUsage(this)
      
      // Legacy compression for backwards compatibility
      if (MEMORY_CONFIG.enableLazyLoading) {
        ChocoboLazyLoader.compressInactiveChocobos(this)
      }
      
      // Memory compression complete
    },

    performHistoricalDataCleanup() {
      // Archive old race data
      HistoricalStatsService.archiveOldRaces(this)
      
      // Clean up historical data
      HistoricalStatsService.cleanupHistoricalData(this)
    },

    scheduleMemoryCheck() {
      // Schedule a memory check for the next idle period
      MemoryManager.scheduleMemoryCheck(this)
    },

    forceMemoryCleanup() {
      // Manual memory cleanup requested
      MemoryManager.forceCleanup(this)
      this.performHistoricalDataCleanup()
    },

    emergencyMemoryCleanup() {
      // Emergency memory cleanup requested
      MemoryManager.emergencyCleanup(this)
      this.performHistoricalDataCleanup()
      
      // Additional emergency measures
      if (this.recentTransactions.length > 5) {
        this.recentTransactions = this.recentTransactions.slice(-5)
      }
      if (this.retirementHistory.length > 20) {
        this.retirementHistory = this.retirementHistory.slice(-20)
      }
    },

    setupPerformanceCallbacks() {
      // Register callback for performance warnings
      PerformanceMonitor.registerWarningCallback((warning) => {
        this.handlePerformanceWarning(warning)
      })

      // Make game store available globally for emergency cleanup
      if (typeof window !== 'undefined') {
        window.gameStore = this
        // Add debug helper
        window.debugMonthlyAwards = () => {
          MonthlyRaceManager.debugPlayerRaces(this)
        }
      }
    },

    handlePerformanceWarning(warning) {
      // Performance warning handled
      
      switch (warning.type) {
        case 'memory':
          if (warning.level === 'critical') {
            // Automatic emergency cleanup for critical memory issues
            setTimeout(() => {
              this.emergencyMemoryCleanup()
            }, 1000)
          } else if (warning.level === 'warning') {
            // Trigger moderate cleanup for warnings
            setTimeout(() => {
              this.forceMemoryCleanup()
            }, 5000)
          }
          break
          
        case 'performance':
          if (warning.data.type === 'lag_spike') {
            // Reduce background race frequency temporarily
            this.temporarilyReduceBackgroundRaces()
          }
          break
      }
    },

    temporarilyReduceBackgroundRaces() {
      // This could temporarily override MEMORY_CONFIG.backgroundRacesPerDay
      // Implementation would depend on how background races are scheduled
    },

    performBreedingDataCleanup() {
      BreedingPoolManager.cleanupBreedingData(this)
    },

    deleteSaveGame() {
      try {
        localStorage.removeItem(StorageManager.STORAGE_KEY)
        return true
      } catch (error) {
        console.error('Failed to delete save:', error)
        return false
      }
    },

    initializeChocoboAges() {
      // Initialize estimated ages for chocobos that don't have proper birth days
      this.chocobos.forEach(chocobo => {
        if ((!chocobo.birthDay || chocobo.birthDay <= 0) && !chocobo.estimatedBirthDay) {
          chocobo.estimatedBirthDay = AgingSystem.generateEstimatedBirthDay(chocobo, this.currentDay)
        }
        
        // Ensure chocobo has age methods if missing (for backward compatibility)
        if (typeof chocobo.getFormattedAge !== 'function') {
          // Re-create as proper Chocobo instance
          const { Chocobo } = require('@/models/chocobos')
          Object.setPrototypeOf(chocobo, Chocobo.prototype)
        }
      })
    },

    validateChocoboBehaviors() {
      // Validate and fix any chocobos with invalid behavior IDs
      this.chocobos.forEach(chocobo => {
        if (!chocobo.behaviorId || chocobo.behaviorId < 1 || chocobo.behaviorId > 6) {
          console.warn(`Fixing invalid behaviorId ${chocobo.behaviorId} for chocobo ${chocobo.name}`)
          chocobo.behaviorId = 3 // Default to STEADY
        }
      })
    }
  },

  getters: {
    availableAuctions: (state) => {
      return state.activeAuctions.filter(auction => 
        auction.endDay >= state.currentDay
      )
    },

    urgentAuctions: (state) => {
      return state.activeAuctions.filter(auction => 
        auction.urgent && auction.endDay >= state.currentDay
      )
    },

    getChocoboById: (state) => (id) => {
      return state.chocobos.find(chocobo => chocobo.id === id)
    },

    getOwnerById: (state) => (id) => {
      return state.owners.find(owner => owner.id === id)
    },

    memoryReport: (state) => {
      return MemoryManager.getMemoryReport(state)
    },

    globalLeaderboards: (state) => {
      return HistoricalStatsService.generateGlobalLeaderboards(state)
    },

    historicalTrends: (state) => {
      return HistoricalStatsService.getHistoricalTrends(state)
    },

    getJockeyById: (state) => (id) => {
      return state.jockeys.find(jockey => jockey.id === id)
    },

    getChocobosByOwnerId: (state) => (ownerId) => {
      const owner = state.owners.find(owner => owner.id === ownerId)
      if (!owner) return []
      return state.chocobos.filter(chocobo => owner.chocoboIds.includes(chocobo.id))
    },

    playerChocobos: (state) => {
      if (!state.playerOwner) return []
      return state.chocobos.filter(chocobo => 
        state.playerOwner.chocoboIds.includes(chocobo.id)
      )
    },

    canAddMoreChocobos: (state) => {
      if (!state.playerOwner) return false
      return state.playerOwner.chocoboIds.length < state.MAX_PLAYER_CHOCOBOS
    },

    hasSavedGame: () => {
      return !!localStorage.getItem(StorageManager.STORAGE_KEY)
    },

    breedingPoolPartners: (state) => (selectedChocobo) => {
      return BreedingPoolManager.getAvailablePartners(selectedChocobo, state)
    },

    breedingPoolStats: (state) => {
      return BreedingPoolManager.getPoolStatistics(state)
    },

    unreadBreedingIncome: (state) => {
      return state.breedingIncomeNotifications?.filter(notification => !notification.read) || []
    },

    topPerformers: (state) => (timeType = 'monthly', raceType = 'short') => {
      return PerformanceRecordsService.getTopPerformers(state.performanceRecords, timeType, raceType)
    },

    performanceStats: (state) => {
      return PerformanceRecordsService.getRecordStats(state.performanceRecords)
    },

    chocoboPerformanceRecords: (state) => (chocoboId) => {
      return PerformanceRecordsService.getChocoboRecords(state.performanceRecords, chocoboId)
    },
    
    unreadPerformanceNotifications: (state) => {
      return (state.performanceNotifications || []).filter(n => !n.read)
    }
  }
})